import React, { useEffect, useState } from "react";
import { refreshMe, updateRestaurantSettings, exportGuestData, eraseGuestData, runRetention, getRole, API_URL } from "../services/api";
import { useT } from "../i18n/useT";

// Data protection (Ethiopia Personal Data Protection Proclamation 1321/2024).
// Manager-only. Three things: how long guest data is kept before it is
// auto-anonymised (storage limitation), a way to export one guest's data
// (right of access), and a way to erase one guest's data (right to erasure).
const S = {
  en: {
    title: "Data protection", subtitle: "Guest data rights under Proclamation 1321/2024 — retention, export and erasure.",
    notice_link: "View the public consent & privacy notice ↗",
    ret: "Data retention", ret_desc: "Automatically anonymise a guest's personal data this many months after they check out. Their revenue and booking records stay; only identifying details (name, phone, ID, address) are removed. Leave empty to keep data.",
    months: "Months to keep after check-out", keep: "Keep forever", save: "Save", saved: "✓ Saved.",
    run: "Run cleanup now", run_desc: "Preview how many old stays would be anonymised, then run it.",
    preview: "Preview", purge: "Anonymise now", purge_confirm: "Anonymise {n} old stay(s)? This cannot be undone.",
    cand: "{n} stay(s) older than {m} months would be anonymised (checked out before {d}).",
    none: "No stays are old enough to anonymise.", no_ret: "Set a retention period first.",
    done: "✓ {n} stay(s) anonymised.",
    export: "Export a guest's data", export_desc: "Download every personal-data field held for one reservation, for a guest access request.",
    resid: "Reservation ID", export_btn: "Export", export_ok: "✓ Downloaded.",
    erase: "Erase a guest's data", erase_desc: "Anonymise one reservation now. The booking survives but can no longer identify the person. Not allowed while the guest is in-house.",
    reason: "Reason (optional)", erase_btn: "Erase this guest", erase_confirm: "Anonymise reservation #{n}? This cannot be undone.",
    erase_ok: "✓ Guest data anonymised.", inhouse: "Guest is currently in-house — check them out first.",
    notfound: "Reservation not found.", err: "Something went wrong. Try again.",
    note: "For the legal wording of your privacy notice (what guests agree to), speak with your ODRA contact — this page is the technical machinery only.",
  },
  am: {
    title: "የመረጃ ጥበቃ", subtitle: "በአዋጅ 1321/2024 መሠረት የእንግዳ መረጃ መብቶች — ማቆያ፣ ወደ ውጭ ማውጣትና ማጥፋት።",
    notice_link: "የይፋ ፈቃድና የግላዊነት ማስታወቂያ ይመልከቱ ↗",
    ret: "የመረጃ ማቆያ", ret_desc: "እንግዳው ከወጣ በኋላ የግል መረጃውን ከዚህ ወራት በኋላ በራስ-ሰር ማንነት-አልባ አድርግ። የገቢና የቦታ ማስያዣ መዝገቦች ይቀራሉ፤ የሚለዩ ዝርዝሮች (ስም፣ ስልክ፣ መታወቂያ፣ አድራሻ) ብቻ ይወገዳሉ። መረጃ ለማቆየት ባዶ ተውት።",
    months: "ከመውጣት በኋላ የሚቆይ ወራት", keep: "ለዘላለም አቆይ", save: "አስቀምጥ", saved: "✓ ተቀምጧል።",
    run: "አሁን ማጽዳት አካሂድ", run_desc: "ስንት አሮጌ ቆይታዎች ማንነት-አልባ እንደሚሆኑ ቅድመ-እይታ አሳይ፣ ከዚያ አካሂድ።",
    preview: "ቅድመ-እይታ", purge: "አሁን ማንነት-አልባ አድርግ", purge_confirm: "{n} አሮጌ ቆይታ(ዎች) ማንነት-አልባ ይደረግ? ይህ አይመለስም።",
    cand: "ከ{m} ወራት በላይ ያሉ {n} ቆይታ(ዎች) ማንነት-አልባ ይሆናሉ ({d} በፊት የወጡ)።",
    none: "ማንነት-አልባ ለማድረግ የበቁ ቆይታዎች የሉም።", no_ret: "መጀመሪያ የማቆያ ጊዜ ያዘጋጁ።",
    done: "✓ {n} ቆይታ(ዎች) ማንነት-አልባ ሆነዋል።",
    export: "የእንግዳ መረጃ ወደ ውጭ አውጣ", export_desc: "ለአንድ ቦታ ማስያዣ የተያዘውን እያንዳንዱን የግል መረጃ አውርድ።",
    resid: "የቦታ ማስያዣ መለያ", export_btn: "አውጣ", export_ok: "✓ ወርዷል።",
    erase: "የእንግዳ መረጃ አጥፋ", erase_desc: "አንድ ቦታ ማስያዣ አሁን ማንነት-አልባ አድርግ። ቦታ ማስያዣው ይቀራል ግን ሰውየውን መለየት አይችልም። እንግዳው በውስጥ እያለ አይፈቀድም።",
    reason: "ምክንያት (አማራጭ)", erase_btn: "ይህን እንግዳ አጥፋ", erase_confirm: "ቦታ ማስያዣ #{n} ማንነት-አልባ ይደረግ? ይህ አይመለስም።",
    erase_ok: "✓ የእንግዳ መረጃ ማንነት-አልባ ሆኗል።", inhouse: "እንግዳው አሁን በውስጥ ነው — መጀመሪያ ያስወጡት።",
    notfound: "ቦታ ማስያዣ አልተገኘም።", err: "ስህተት ተፈጥሯል። እንደገና ይሞክሩ።",
    note: "ለግላዊነት ማስታወቂያዎ ሕጋዊ ቃል (እንግዶች የሚስማሙበት) ከODRA ግንኙነትዎ ጋር ይነጋገሩ — ይህ ገጽ ቴክኒካዊ መሣሪያ ብቻ ነው።",
  },
  om: {
    title: "Eegumsa daataa", subtitle: "Mirga daataa keessummaa Labsii 1321/2024 jalatti — turtii, gadi baasuu fi haquu.",
    notice_link: "Beeksisa hayyama fi iccitii ummataa ilaali ↗",
    ret: "Turtii daataa", ret_desc: "Erga keessummaan ba'ee ji'oota kana booda daataa dhuunfaa isaa ofumaan maqaa-dhabsiisi. Galiin fi galmeen booking ni turu; qofa waan nama addaan baasu (maqaa, bilbila, ID, teessoo) haqama. Daataa turfachuuf duwwaa dhiisi.",
    months: "Ji'oota erga ba'anii turan", keep: "Bara baraan turi", save: "Olkaa'i", saved: "✓ Olkaa'ame.",
    run: "Amma qulqulleessi", run_desc: "Turtiiwwan moofaa meeqni maqaa-dhabsiifamu duraan ilaali, ergasii raawwadhu.",
    preview: "Duraa ilaali", purge: "Amma maqaa-dhabsiisi", purge_confirm: "Turtii moofaa {n} maqaa-dhabsiisuu? Kun deebi'uu hin danda'u.",
    cand: "Turtiiwwan ji'oota {m} ol turan {n} maqaa-dhabsiifamu ({d} dura ba'an).",
    none: "Turtiiwwan maqaa-dhabsiifamuuf ga'an hin jiran.", no_ret: "Duraan yeroo turtii qindeessi.",
    done: "✓ Turtii {n} maqaa-dhabsiifame.",
    export: "Daataa keessummaa gadi baasi", export_desc: "Booking tokkoof odeeffannoo dhuunfaa qabame hunda buufadhu.",
    resid: "ID booking", export_btn: "Gadi baasi", export_ok: "✓ Buufame.",
    erase: "Daataa keessummaa haqi", erase_desc: "Booking tokko amma maqaa-dhabsiisi. Bookingichi ni tura garuu nama addaan baasuu hin danda'u. Keessummaan keessa jiru yoo ta'e hin hayyamamu.",
    reason: "Sababa (filannoo)", erase_btn: "Keessummaa kana haqi", erase_confirm: "Booking #{n} maqaa-dhabsiisuu? Kun deebi'uu hin danda'u.",
    erase_ok: "✓ Daataan keessummaa maqaa-dhabsiifame.", inhouse: "Keessummaan amma keessa jira — duraan baasi.",
    notfound: "Booking hin argamne.", err: "Wanti dogoggore. Irra deebi'i yaali.",
    note: "Jecha seeraa beeksisa iccitii keetiif (waan keessummoonni irratti waliigalan) qunnamtii ODRA kee waliin dubbadhu — fuulli kun meeshaa teeknikaa qofa.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)", padding: 18, marginTop: 14 };
const inp = { padding: 10, borderRadius: 9, border: "1px solid #d8dee2", fontSize: 14, width: "100%", boxSizing: "border-box" };
const lbl = { fontSize: 12.5, fontWeight: 600, color: "#334155", display: "block", margin: "10px 0 4px" };

export default function PrivacyPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const fill = (k, vars) => Object.entries(vars).reduce((s, [key, v]) => s.replace(`{${key}}`, v), t(k));
  const role = getRole() || "manager";
  const isManager = role === "manager";

  const [months, setMonths] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState({ ret: "", run: "", exp: "", era: "" });
  const [candidates, setCandidates] = useState(null);   // preview result
  const [expId, setExpId] = useState("");
  const [eraId, setEraId] = useState("");
  const [eraReason, setEraReason] = useState("");

  useEffect(() => {
    refreshMe().then((d) => setMonths(d.data_retention_months ? String(d.data_retention_months) : "")).catch(() => {});
  }, []);

  const saveRet = async () => {
    setBusy("ret"); setMsg((m) => ({ ...m, ret: "" }));
    try {
      const val = months === "" ? 0 : Math.max(0, parseInt(months, 10) || 0);
      await updateRestaurantSettings({ data_retention_months: val });
      setMsg((m) => ({ ...m, ret: t("saved") }));
    } catch { setMsg((m) => ({ ...m, ret: t("err") })); } finally { setBusy(""); }
  };

  const preview = async () => {
    setBusy("run"); setMsg((m) => ({ ...m, run: "" })); setCandidates(null);
    try {
      const r = await runRetention(true);
      if (!r.enabled) { setMsg((m) => ({ ...m, run: t("no_ret") })); return; }
      setCandidates(r.candidates);
      setMsg((m) => ({ ...m, run: r.candidates ? fill("cand", { n: r.candidates, m: r.retention_months, d: r.cutoff }) : t("none") }));
    } catch { setMsg((m) => ({ ...m, run: t("err") })); } finally { setBusy(""); }
  };
  const purge = async () => {
    if (!window.confirm(fill("purge_confirm", { n: candidates }))) return;
    setBusy("run");
    try { const r = await runRetention(false); setMsg((m) => ({ ...m, run: fill("done", { n: r.anonymised }) })); setCandidates(null); }
    catch { setMsg((m) => ({ ...m, run: t("err") })); } finally { setBusy(""); }
  };

  const doExport = async () => {
    setBusy("exp"); setMsg((m) => ({ ...m, exp: "" }));
    try {
      const r = await exportGuestData(parseInt(expId, 10));
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `guest_${expId}_data.json`; a.click(); URL.revokeObjectURL(url);
      setMsg((m) => ({ ...m, exp: t("export_ok") }));
    } catch (e) { setMsg((m) => ({ ...m, exp: e?.response?.status === 404 ? t("notfound") : t("err") })); } finally { setBusy(""); }
  };
  const doErase = async () => {
    if (!window.confirm(fill("erase_confirm", { n: eraId }))) return;
    setBusy("era"); setMsg((m) => ({ ...m, era: "" }));
    try { await eraseGuestData(parseInt(eraId, 10), eraReason); setMsg((m) => ({ ...m, era: t("erase_ok") })); setEraId(""); setEraReason(""); }
    catch (e) {
      const code = e?.response?.status;
      setMsg((m) => ({ ...m, era: code === 409 ? t("inhouse") : code === 404 ? t("notfound") : t("err") }));
    } finally { setBusy(""); }
  };

  if (!isManager) return <div style={{ maxWidth: 640, margin: "0 auto" }}><h1>{t("title")}</h1><p className="muted">{t("subtitle")}</p></div>;

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
      <div style={{ fontSize: 13.5, marginTop: 6 }}>
        <a href={`${API_URL}/privacy/notice`} target="_blank" rel="noopener noreferrer" style={{ color: "#0F6E56" }}>{t("notice_link")}</a>
      </div>

      {/* retention */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("ret")}</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("ret_desc")}</div>
        <label style={lbl}>{t("months")}</label>
        <input style={{ ...inp, maxWidth: 220 }} inputMode="numeric" placeholder={t("keep")} value={months}
               onChange={(e) => setMonths(e.target.value.replace(/\D/g, ""))} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn primary" disabled={busy === "ret"} onClick={saveRet}>{t("save")}</button>
          {msg.ret && <span style={{ fontSize: 13, color: msg.ret.startsWith("✓") ? "#1D7A44" : "#A32D2D" }}>{msg.ret}</span>}
        </div>
      </div>

      {/* retention run */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("run")}</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("run_desc")}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn small" disabled={busy === "run"} onClick={preview}>{t("preview")}</button>
          {candidates > 0 && <button className="btn primary small" disabled={busy === "run"} onClick={purge}>{t("purge")}</button>}
        </div>
        {msg.run && <div style={{ fontSize: 13, marginTop: 10, color: msg.run.startsWith("✓") ? "#1D7A44" : "#334155" }}>{msg.run}</div>}
      </div>

      {/* export */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("export")}</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("export_desc")}</div>
        <label style={lbl}>{t("resid")}</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input style={{ ...inp, maxWidth: 160 }} inputMode="numeric" value={expId} onChange={(e) => setExpId(e.target.value.replace(/\D/g, ""))} />
          <button className="btn small" disabled={busy === "exp" || !expId} onClick={doExport}>{t("export_btn")}</button>
          {msg.exp && <span style={{ fontSize: 13, color: msg.exp.startsWith("✓") ? "#1D7A44" : "#A32D2D" }}>{msg.exp}</span>}
        </div>
      </div>

      {/* erase */}
      <div style={CARD}>
        <div style={{ fontWeight: 700, color: "#063b48" }}>{t("erase")}</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t("erase_desc")}</div>
        <label style={lbl}>{t("resid")}</label>
        <input style={{ ...inp, maxWidth: 160 }} inputMode="numeric" value={eraId} onChange={(e) => setEraId(e.target.value.replace(/\D/g, ""))} />
        <label style={lbl}>{t("reason")}</label>
        <input style={inp} value={eraReason} onChange={(e) => setEraReason(e.target.value)} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button className="btn small" style={{ background: "#A32D2D", color: "#fff", borderColor: "#A32D2D" }} disabled={busy === "era" || !eraId} onClick={doErase}>{t("erase_btn")}</button>
          {msg.era && <span style={{ fontSize: 13, color: msg.era.startsWith("✓") ? "#1D7A44" : "#A32D2D" }}>{msg.era}</span>}
        </div>
      </div>

      <div className="muted" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.5 }}>{t("note")}</div>
    </div>
  );
}
